// src/components/meetings/AgendaForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Plus, X, Save, Clock, User, Edit, Trash2, ChevronDown, 
  ChevronUp, FileText, Link, ArrowRight, ArrowLeft, Building,
  Briefcase, Target, CheckSquare, FileBarChart
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { db } from '../../firebase/config';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';

const AgendaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  
  const [meeting, setMeeting] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const [isEditing, setIsEditing] = useState({});
  const [businessReferences, setBusinessReferences] = useState({
    projects: [],
    departments: [],
    policies: [],
    reports: [],
    initiatives: []
  });

  // Fetch meeting and agenda data
  useEffect(() => {
    const fetchMeeting = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const meetingDoc = await getDoc(doc(db, "meetings", id));
        
        if (meetingDoc.exists()) {
          const data = meetingDoc.data();
          const meetingData = {
            id: meetingDoc.id,
            ...data,
            date: data.date?.toDate() || null,
            endTime: data.endTime?.toDate() || null,
            createdAt: data.createdAt?.toDate() || null,
            updatedAt: data.updatedAt?.toDate() || null
          };
          
          setMeeting(meetingData);
          
          // Initialize agenda items from meeting data or with empty array
          if (Array.isArray(meetingData.agendaItems) && meetingData.agendaItems.length > 0) {
            setAgendaItems(meetingData.agendaItems);
          } else {
            setAgendaItems([{ 
              id: `a${Date.now()}`, 
              title: '', 
              description: '', 
              duration: 15, 
              presenter: '', 
              relatedTo: null,
              type: 'discussion'
            }]);
          }
        } else {
          setError("Meeting not found");
        }
      } catch (err) {
        console.error("Error fetching meeting:", err);
        setError("Failed to load meeting data");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMeeting();
  }, [id]);

  // Fetch business-related references
  useEffect(() => {
    const fetchBusinessReferences = async () => {
      if (!activeBusiness?.id) return;
      
      try {
        // Fetch projects
        const projectsRef = collection(db, "projects");
        const projectsQuery = query(projectsRef, where("businessId", "==", activeBusiness.id));
        const projectsSnapshot = await getDocs(projectsQuery);
        const projects = projectsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          status: doc.data().status
        }));

        // Fetch departments (from employees)
        const employeesRef = collection(db, "employees");
        const employeesQuery = query(employeesRef, where("businessId", "==", activeBusiness.id));
        const employeesSnapshot = await getDocs(employeesQuery);
        const departments = [...new Set(employeesSnapshot.docs
          .map(doc => doc.data().department)
          .filter(dept => dept)
        )].map(dept => ({ id: dept.toLowerCase().replace(/\s+/g, '_'), name: dept }));

        // Mock business references (these would normally come from your database)
        const mockReferences = {
          policies: [
            { id: 'pol1', name: 'HR Policy Manual' },
            { id: 'pol2', name: 'Financial Procedures' },
            { id: 'pol3', name: 'IT Security Policy' },
            { id: 'pol4', name: 'Health & Safety Guidelines' },
            { id: 'pol5', name: 'Code of Conduct' }
          ],
          reports: [
            { id: 'rep1', name: 'Quarterly Financial Report' },
            { id: 'rep2', name: 'Sales Performance Review' },
            { id: 'rep3', name: 'Operational Metrics' },
            { id: 'rep4', name: 'Employee Satisfaction Survey' },
            { id: 'rep5', name: 'Market Analysis Report' }
          ],
          initiatives: [
            { id: 'ini1', name: 'Digital Transformation' },
            { id: 'ini2', name: 'Sustainability Program' },
            { id: 'ini3', name: 'Customer Experience Enhancement' },
            { id: 'ini4', name: 'Workforce Development' },
            { id: 'ini5', name: 'Process Optimization' }
          ]
        };

        setBusinessReferences({
          projects,
          departments,
          ...mockReferences
        });
        
      } catch (err) {
        console.error("Error fetching business references:", err);
      }
    };

    fetchBusinessReferences();
  }, [activeBusiness]);

  // Agenda item types
  const agendaItemTypes = [
    { id: 'discussion', label: 'Discussion', icon: FileText },
    { id: 'decision', label: 'Decision Required', icon: CheckSquare },
    { id: 'presentation', label: 'Presentation', icon: FileBarChart },
    { id: 'review', label: 'Review', icon: Target },
    { id: 'update', label: 'Status Update', icon: Briefcase },
    { id: 'other', label: 'Other', icon: FileText }
  ];

  // Add a new agenda item
  const addAgendaItem = () => {
    const newItem = {
      id: `a${Date.now()}`,
      title: '',
      description: '',
      duration: 15,
      presenter: '',
      relatedTo: null,
      type: 'discussion'
    };
    
    setAgendaItems([...agendaItems, newItem]);
    setExpandedItem(newItem.id);
    setIsEditing({ ...isEditing, [newItem.id]: true });
  };

  // Remove an agenda item
  const removeAgendaItem = (itemId) => {
    setAgendaItems(agendaItems.filter(item => item.id !== itemId));
    if (expandedItem === itemId) {
      setExpandedItem(null);
    }
  };

  // Update an agenda item
  const updateAgendaItem = (itemId, field, value) => {
    setAgendaItems(agendaItems.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  // Set related reference for an agenda item
  const setItemReference = (itemId, type, referenceId) => {
    const reference = businessReferences[type].find(ref => ref.id === referenceId);
    
    if (reference) {
      setAgendaItems(agendaItems.map(item => 
        item.id === itemId ? { 
          ...item, 
          relatedTo: { type, id: referenceId, name: reference.name } 
        } : item
      ));
    }
  };

  // Remove related reference from an agenda item
  const removeItemReference = (itemId) => {
    setAgendaItems(agendaItems.map(item => 
      item.id === itemId ? { ...item, relatedTo: null } : item
    ));
  };

  // Save agenda items to meeting
  const saveAgenda = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const meetingRef = doc(db, "meetings", id);
      await updateDoc(meetingRef, {
        agendaItems: agendaItems,
        updatedAt: serverTimestamp()
      });
      
      setSuccess('Agenda saved successfully');
      
      // Clear editing states
      setIsEditing({});
      setExpandedItem(null);
      
    } catch (err) {
      console.error('Error saving agenda:', err);
      setError('Failed to save agenda');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate total meeting duration
  const getTotalDuration = () => {
    return agendaItems.reduce((total, item) => total + (item.duration || 0), 0);
  };

  // Format duration for display
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`;
    }
  };

  // Get agenda item type details
  const getItemTypeDetails = (type) => {
    const itemType = agendaItemTypes.find(t => t.id === type);
    return itemType || agendaItemTypes[0];
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#190192]"></div>
        <p className="ml-2">Loading agenda...</p>
      </div>
    );
  }

  if (error && !meeting) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => navigate('/meetings')}
            className="mt-3 text-red-600 hover:text-red-800 underline"
          >
            Return to meetings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/meetings/details/${id}`)}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Meeting Agenda</h1>
                {meeting && (
                  <p className="text-sm text-gray-500 mt-1">
                    {meeting.title} • {meeting.date ? format(meeting.date, 'MMM d, yyyy • h:mm a') : 'Not scheduled'}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-sm text-gray-500">
                Total Duration: <span className="font-medium">{formatDuration(getTotalDuration())}</span>
              </div>
              {activeBusiness && (
                <div className="flex items-center text-sm text-gray-500">
                  <Building className="h-4 w-4 mr-1" />
                  {activeBusiness.name}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-green-600">{success}</p>
        </div>
      )}

      {/* Agenda Items */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Agenda Items</h2>
            <button
              onClick={addAgendaItem}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#190192] hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {agendaItems.map((item, index) => {
            const isExpanded = expandedItem === item.id;
            const isEditingItem = isEditing[item.id];
            const itemTypeDetails = getItemTypeDetails(item.type);
            const ItemIcon = itemTypeDetails.icon;

            return (
              <div key={item.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Item Header */}
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                        {index + 1}
                      </span>
                      
                      {isEditingItem ? (
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) => updateAgendaItem(item.id, 'title', e.target.value)}
                          placeholder="Agenda item title"
                          className="flex-1 text-lg font-medium bg-transparent border-b border-gray-300 focus:border-blue-500 focus:outline-none"
                        />
                      ) : (
                        <h3 className="text-lg font-medium text-gray-900">
                          {item.title || 'Untitled Agenda Item'}
                        </h3>
                      )}

                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <ItemIcon className="h-3 w-3 mr-1" />
                          {itemTypeDetails.label}
                        </span>
                        
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <Clock className="h-3 w-3 mr-1" />
                          {item.duration || 15}m
                        </span>
                      </div>
                    </div>

                    {/* Item Details */}
                    {isExpanded && (
                      <div className="ml-11 space-y-4">
                        {/* Type and Duration */}
                        {isEditingItem && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Item Type
                              </label>
                              <select
                                value={item.type || 'discussion'}
                                onChange={(e) => updateAgendaItem(item.id, 'type', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              >
                                {agendaItemTypes.map(type => (
                                  <option key={type.id} value={type.id}>{type.label}</option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Duration (minutes)
                              </label>
                              <input
                                type="number"
                                min="5"
                                max="180"
                                step="5"
                                value={item.duration || 15}
                                onChange={(e) => updateAgendaItem(item.id, 'duration', parseInt(e.target.value) || 15)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>
                          </div>
                        )}

                        {/* Description */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          {isEditingItem ? (
                            <textarea
                              rows={3}
                              value={item.description || ''}
                              onChange={(e) => updateAgendaItem(item.id, 'description', e.target.value)}
                              placeholder="Describe what will be discussed..."
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <p className="text-sm text-gray-600">
                              {item.description || 'No description provided'}
                            </p>
                          )}
                        </div>

                        {/* Presenter */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Presenter
                          </label>
                          {isEditingItem ? (
                            <input
                              type="text"
                              value={item.presenter || ''}
                              onChange={(e) => updateAgendaItem(item.id, 'presenter', e.target.value)}
                              placeholder="Who will present this item?"
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          ) : (
                            <p className="text-sm text-gray-600 flex items-center">
                              <User className="h-4 w-4 mr-1" />
                              {item.presenter || 'No presenter assigned'}
                            </p>
                          )}
                        </div>

                        {/* Related References */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Related To
                          </label>
                          
                          {item.relatedTo ? (
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                                <Link className="h-4 w-4 mr-1" />
                                {item.relatedTo.name}
                                <span className="ml-1 text-xs">({item.relatedTo.type})</span>
                              </span>
                              {isEditingItem && (
                                <button
                                  onClick={() => removeItemReference(item.id)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ) : isEditingItem ? (
                            <div className="space-y-2">
                              {Object.entries(businessReferences).map(([type, refs]) => (
                                refs.length > 0 && (
                                  <div key={type}>
                                    <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">
                                      {type}
                                    </label>
                                    <select
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          setItemReference(item.id, type, e.target.value);
                                        }
                                      }}
                                      className="block w-full px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="">Select {type}...</option>
                                      {refs.map(ref => (
                                        <option key={ref.id} value={ref.id}>
                                          {ref.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                )
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">No related items</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </button>

                    <button
                      onClick={() => setIsEditing({ ...isEditing, [item.id]: !isEditingItem })}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => removeAgendaItem(item.id)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {agendaItems.length === 0 && (
            <div className="p-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No agenda items</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first agenda item.</p>
              <button
                onClick={addAgendaItem}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Item
              </button>
            </div>
          )}
        </div>

        {/* Save Button */}
        {agendaItems.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <p className="text-sm text-gray-600">
                  {agendaItems.length} agenda item{agendaItems.length !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-gray-600">
                  Total duration: {formatDuration(getTotalDuration())}
                </p>
              </div>
              
              <button
                onClick={saveAgenda}
                disabled={isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#190192] hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Agenda
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgendaForm;